import { Box, Typography, IconButton, Tooltip } from "@mui/joy";
import { PanelLeftOpen, MessageSquare } from "lucide-react";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { Notes as NotesWidget } from "../../../../components";
import { useResponsive } from "../../../../hooks";
import { MobileDrawer } from "../../../../shared";

const NOTES_COLLAPSED_KEY = "project-notes-collapsed";

function readNotesCollapsed(): boolean {
    try {
        const stored = localStorage.getItem(NOTES_COLLAPSED_KEY);
        if (stored !== null) return stored === "true";
    } catch {
        // ignore storage errors
    }
    return true;
}

export function Notes() {
    const { isTablet, isMobile } = useResponsive();
    const [collapsed, setCollapsedState] = useState(readNotesCollapsed);
    const setCollapsed = useCallback(
        (value: boolean | ((prev: boolean) => boolean)) => {
            setCollapsedState((prev) => {
                const next = typeof value === "function" ? value(prev) : value;
                try {
                    localStorage.setItem(NOTES_COLLAPSED_KEY, String(next));
                } catch {
                    // ignore storage errors
                }
                return next;
            });
        },
        [],
    );
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const { id } = useParams<{ id: string }>();

    if (!id) return null;

    if (isTablet || isMobile) {
        return (
            <>
                {!isDrawerOpen && (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => setIsDrawerOpen(true)}
                        sx={{
                            position: "fixed",
                            bottom: isMobile
                                ? "calc(65px + env(safe-area-inset-bottom, 0px))"
                                : 16,
                            right: 16,
                            zIndex: 1000,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.25,
                            py: 1,
                            m: 0,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "4px",
                            bgcolor: "background.surface",
                            color: "text.primary",
                            //boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                            cursor: "pointer",
                            transition:
                                "transform 0.15s ease, box-shadow 0.15s ease",
                            "&:active": {
                                transform: "scale(0.96)",
                                boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 28,
                                height: 28,
                                borderRadius: "4px",
                                bgcolor: "primary.softBg",
                                color: "primary.solidBg",
                                flexShrink: 0,
                            }}
                        >
                            <MessageSquare size={16} />
                        </Box>
                        <Typography
                            level="body-sm"
                            sx={{ fontWeight: 600, lineHeight: 1, pr: 0.25 }}
                        >
                            Notes
                        </Typography>
                    </Box>
                )}
                <MobileDrawer
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    title="Notes & Feed"
                >
                    <Box height={"100%"} sx={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <NotesWidget
                            entityType="project"
                            entityId={id}
                            density="compact"
                            hideHeader={true}
                            active={isDrawerOpen}
                        />
                    </Box>
                </MobileDrawer>
            </>
        );
    }

    return (
        <Box
            sx={{
                position: "sticky",
                top: 0,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.surface",
                height: "100%",
                flexShrink: 0,
                borderLeft: "1px solid",
                borderColor: "divider",
            }}
            width={collapsed ? 48 : 360}
        >
            <Box
                className="sidebar-header"
                sx={{
                    px: collapsed ? 0 : 1.5,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    minHeight: 48,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.level1",
                }}
            >
                {!collapsed && (
                    <Typography level="title-md">Notes & Feed</Typography>
                )}

                <IconButton
                    variant="plain"
                    color="neutral"
                    size="sm"
                    onClick={() => setCollapsed((v) => !v)}
                    sx={{ borderRadius: "4px" }}
                >
                    <PanelLeftOpen size={18} />
                </IconButton>
            </Box>

            {collapsed ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        py: 2,
                        bgcolor: "background.surface",
                        cursor: "pointer",
                    }}
                    onClick={() => setCollapsed(false)}
                >
                    <Tooltip
                        title="Expand Notes"
                        placement="left"
                        variant="soft"
                    >
                        <Box sx={{ opacity: 0.4 }}>
                            <MessageSquare size={20} />
                        </Box>
                    </Tooltip>
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        overflow: "hidden",
                    }}
                >
                    <NotesWidget
                        entityType="project"
                        entityId={id}
                        density="compact"
                        hideHeader={true}
                    />
                </Box>
            )}
        </Box>
    );
}
